<template>
  <div
    class="integration-card snelstart-card"
    :class="{
      'integration-card--connected': isActive,
      'integration-card--idle': !isConfigured,
    }"
    @click="handleOpen"
  >
    <div class="integration-card__content">
      <div class="integration-card__icon">
        <div class="integration-card__icon-inner">
          <img :src="snelstartIcon" :alt="$t('snelstart.name')" />
        </div>
      </div>
      <div class="integration-card__info">
        <h3 class="integration-card__title">{{ $t('snelstart.name') }}</h3>
        <p class="integration-card__description">
          {{ $t('snelstart.description') }}
        </p>
      </div>
      <div class="integration-card__actions">
        <div class="integration-card__status" :class="statusBadgeClass">
          {{ statusLabel }}
        </div>
        <Btn
          :variant="isConfigured ? 'tertiary' : 'primary'"
          size="sm"
          :loading="loading"
          @click.stop="handleOpen"
        >
          {{
            isConfigured
              ? $t('snelstart.actions.manage')
              : $t('snelstart.actions.connect')
          }}
        </Btn>
      </div>
    </div>
    <div class="integration-card__meta">
      {{ statusHint }}
    </div>
  </div>
</template>

<script>
  import { computed, getCurrentInstance } from 'vue';
  import Btn from '@/components/partials/Btn.vue';

  export default {
    name: 'SnelstartIntegrationCard',

    components: { Btn },

    props: {
      integration: { type: Object, default: null },
      loading: { type: Boolean, default: false },
    },

    emits: ['manage'],

    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const snelstartIcon = new URL(
        '../../assets/img/integrations/snelstart.png',
        import.meta.url
      ).href;

      const isConfigured = computed(() => !!props.integration?.id);
      const isActive = computed(
        () => !!props.integration?.is_active && !!props.integration?.id
      );

      const statusLabel = computed(() => {
        if (isActive.value) return $t('snelstart.status.connected');
        if (isConfigured.value) return $t('snelstart.status.inactive');
        return $t('snelstart.status.not_connected');
      });

      const statusBadgeClass = computed(() => {
        if (isActive.value) return 'integration-card__status--success';
        return 'integration-card__status--idle';
      });

      const statusHint = computed(() => {
        if (isActive.value) return $t('snelstart.status.active_message');
        return $t('snelstart.status.empty_message');
      });

      const handleOpen = () => emit('manage');

      return {
        snelstartIcon,
        isConfigured,
        isActive,
        statusLabel,
        statusBadgeClass,
        statusHint,
        handleOpen,
      };
    },
  };
</script>

<style scoped lang="scss">
  .integration-card {
    background: white;
    border: 1px solid var(--slate-200);
    border-radius: 8px;
    padding: 16px;
    cursor: pointer;
    transition: all 0.2s ease;

    @include hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }

    &__content {
      display: flex;
      align-items: flex-start;
      gap: 16px;
    }

    &__icon {
      width: 40px;
      height: 40px;
      border-radius: 8px;
      border: 1px solid var(--slate-200);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      background: var(--white);
    }

    &__icon-inner {
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 28px;
        height: 28px;
        border-radius: 4px;
      }
    }

    &__info {
      flex: 1;
      min-width: 0;
    }

    &__title {
      font-size: 1rem;
      font-weight: 600;
      margin: 0 0 4px 0;
      color: var(--slate-900);
    }

    &__description {
      margin: 0;
      font-size: 0.8125rem;
      color: var(--slate-500);
      @include ellipsis(2);
    }

    &__actions {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 0.5rem;
      flex-shrink: 0;
    }

    &__status {
      font-size: 14px;
      padding: 6px 10px;
      border-radius: 2rem;
      color: var(--slate-700);
      background-color: var(--slate-200);
      white-space: nowrap;

      &--success {
        color: var(--green-700);
        background-color: var(--green-100);
      }

      &--idle {
        color: var(--slate-700);
        background-color: var(--slate-200);
      }
    }

    &__meta {
      margin-top: 8px;
      font-size: 0.75rem;
      color: var(--slate-500);
      @include ellipsis(2);
    }

    &--connected {
      border-color: var(--green-200);
    }
  }
</style>
